import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { AlertCircle, CheckCircle, X, ChevronDown, ChevronLeft, ChevronRight, Download, Upload } from 'lucide-react';
import apiService from '../services/api';
import JsonViewer from '../components/JsonViewer';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { useData } from '../contexts/DataContext';
import { ServiceStatus } from '../types/api';

function degreesToCompass(degrees: number | undefined): string {
  if (degrees == null || isNaN(degrees)) return 'N/A';
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}

const ServicesPage: React.FC = () => {
  const { selectedDataId, setSelectedDataId } = useData();
  const [showSolarWebPanel, setShowSolarWebPanel] = useState(false);
  const [showSolcastPanel, setShowSolcastPanel] = useState(false);
  const [showOpenMeteoPanel, setShowOpenMeteoPanel] = useState(false);
  const [showAmberPanel, setShowAmberPanel] = useState(false);

  const [dataEntries, setDataEntries] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const { data: serviceStatus, isLoading: statusLoading } = useQuery<ServiceStatus[]>({
    queryKey: ['serviceStatus'],
    queryFn: (): Promise<ServiceStatus[]> => apiService.getServiceStatus(),
  });
  const services: ServiceStatus[] = serviceStatus ?? [];

  const { data: amberData, isLoading: amberLoading } = useQuery({
    queryKey: ['amberData'],
    queryFn: () => apiService.getAmberData(),
  });

  // Amber price in cents for channelType 'general'
  const amberGeneralPriceCents = (() => {
    const prices = amberData?.data?.current_prices;
    if (Array.isArray(prices)) {
      const general = prices.find((p: any) => p.channelType === 'general');
      if (general && typeof general.perKwh === 'number') {
        return (general.perKwh).toFixed(1); 
      }
    }
    return null;
  })();
  const amberFeedInTarrifCents = (() => {
    const prices = amberData?.data?.current_prices;
    if (Array.isArray(prices)) {
      const feedIn = prices.find((p: any) => p.channelType === 'feedIn');
      if (feedIn && typeof feedIn.perKwh === 'number') {
        return (feedIn.perKwh).toFixed(1);
      }
    }
    return null;
  })();

  const { data: solarWebData, isLoading: solarWebLoading } = useQuery({
    queryKey: ['solarWebData'],
    queryFn: () => apiService.getSolarWebData(),
  });

  const { data: solcastData, isLoading: solcastLoading } = useQuery({
    queryKey: ['solcastData'],
    queryFn: () => apiService.getSolcastData(),
  });

  const { data: openMeteoData, isLoading: weatherLoading } = useQuery({
    queryKey: ['openMeteoData'],
    queryFn: () => apiService.getOpenMeteoData(),
  });

  const { data: dataEntriesData, isLoading: dataEntriesLoading, refetch: refetchDataEntries } = useQuery({
    queryKey: ['dataEntries'],
    queryFn: () => apiService.getUserDataEntries(50),
  });

  const { data: latestData, isLoading: latestDataLoading } = useQuery({
    queryKey: ['latestData', selectedDataId],
    queryFn: () =>
      selectedDataId ? apiService.getUserDataById(selectedDataId) : apiService.getLatestUserData(),
    enabled: !!selectedDataId || dataEntries.length > 0,
  });

  // Set default selected data ID and update data entries
  useEffect(() => {
    if (dataEntriesData?.entries && dataEntriesData.entries.length > 0) {
      setDataEntries(dataEntriesData.entries);
      // Set the first (latest) entry as default if none selected
      if (selectedDataId === null) {
        setSelectedDataId(dataEntriesData.entries[0].id);
      }
    }
  }, [dataEntriesData, selectedDataId, setSelectedDataId]);



  const handlePreviousDataEntry = () => {
    if (dataEntries.length === 0 || selectedDataId === null) return;
    
    const currentIndex = dataEntries.findIndex(entry => entry.id === selectedDataId);
    if (currentIndex === -1 || currentIndex === dataEntries.length - 1) return;
    
    const nextIndex = currentIndex + 1;
    setSelectedDataId(dataEntries[nextIndex].id);
  };

  const handleNextDataEntry = () => {
    if (dataEntries.length === 0 || selectedDataId === null) return;
    
    const currentIndex = dataEntries.findIndex(entry => entry.id === selectedDataId);
    if (currentIndex === -1 || currentIndex === 0) return;
    
    const previousIndex = currentIndex - 1;
    setSelectedDataId(dataEntries[previousIndex].id);
  };

  const handleExportServicesData = async () => {
    setIsExporting(true);
    try {
      const blob = await apiService.exportServicesData();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `services-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Services data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export services data');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportServicesData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      toast.error('Please select a valid JSON file');
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      '⚠️ WARNING: This will DELETE ALL existing services data, historical data, forecast data, and battery schedules for your account.\n\n' +
      'This action cannot be undone. Are you sure you want to continue?'
    );

    if (!confirmed) {
      // Reset the file input if user cancels
      event.target.value = '';
      return;
    }

    setIsImporting(true);
    try {
      await apiService.importServicesData(file);
      // Refresh the data entries after import
      refetchDataEntries();
      toast.success('Services data imported successfully!');
    } catch (error: any) {
      console.error('Import error:', error);
      const errorMsg = error?.response?.data?.detail || error?.message || 'Failed to import services data';
      toast.error(errorMsg);
    } finally {
      setIsImporting(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const isLoading = statusLoading || amberLoading || solcastLoading|| solarWebLoading || weatherLoading || latestDataLoading || dataEntriesLoading;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          {/* Data Entry Selector */}
          <div className="flex items-center space-x-2">
            <label htmlFor="data-entry-select" className="text-sm font-medium text-gray-700">
              Data Entry:
            </label>
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePreviousDataEntry}
                disabled={dataEntriesLoading || dataEntries.length === 0 || selectedDataId === null || dataEntries.findIndex(entry => entry.id === selectedDataId) === dataEntries.length - 1}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed transition-colors"
                title="Previous data entry"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <select
                id="data-entry-select"
                value={selectedDataId || ''}
                onChange={(e) => setSelectedDataId(Number(e.target.value))}
                className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                disabled={dataEntriesLoading}
              >
                {dataEntriesLoading ? (
                  <option>Loading...</option>
                ) : dataEntries.length > 0 ? (
                  dataEntries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {formatInTimeZone(parseISO(entry.created_at), Intl.DateTimeFormat().resolvedOptions().timeZone, 'MMM dd, yyyy HH:mm')} 
                    </option>
                  ))
                ) : (
                  <option>No data available</option>
                )}
              </select>
              <button
                onClick={handleNextDataEntry}
                disabled={dataEntriesLoading || dataEntries.length === 0 || selectedDataId === null || dataEntries.findIndex(entry => entry.id === selectedDataId) === 0}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:text-gray-200 disabled:cursor-not-allowed transition-colors"
                title="Next data entry"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={handleExportServicesData}
            disabled={isExporting}
            className="btn-secondary flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{isExporting ? 'Exporting...' : 'Export Data'}</span>
          </button>
          
          <label className="btn-secondary flex items-center space-x-2 cursor-pointer" title="Import data (will delete all existing data)">
            <Upload className="h-4 w-4" />
            <span>{isImporting ? 'Importing...' : 'Import Data'}</span>
            <input
              type="file"
              accept=".json"
              onChange={handleImportServicesData}
              className="hidden"
            />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Amber Electricity Data */}
        <div 
          className="card cursor-pointer hover:shadow-lg transition-shadow duration-200"
          onClick={() => setShowAmberPanel(true)}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Amber Electricity</h2>
            <div className="flex items-center space-x-2">
              {services.find(s => s.service === 'amber')?.status === 'healthy' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="text-sm text-gray-500">
                {format(new Date(amberData?.data?.timestamp || Date.now()), 'MMM dd HH:mm')}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          {amberData ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Site ID</span>
                <span className="font-medium">{amberData?.data?.site_id ?? 'Unknown'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Price</span>
                <span className="font-medium">
                  {amberGeneralPriceCents !== null ? `${amberGeneralPriceCents}¢/kWh` : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Feed-In Tariff</span>
                <span className="font-medium">
                  {amberFeedInTarrifCents !== null ? `${amberFeedInTarrifCents}¢/kWh` : 'N/A'}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No data available</p>
          )}
        </div>

        {/* SolarWeb Data */}
        <div 
          className="card cursor-pointer hover:shadow-lg transition-shadow duration-200"
          onClick={() => setShowSolarWebPanel(true)}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">SolarWeb</h2>
            <div className="flex items-center space-x-2">
              {services.find(s => s.service === 'solarweb')?.status === 'healthy' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="text-sm text-gray-500">
                {format(new Date(solarWebData?.data?.timestamp || Date.now()), 'MMM dd HH:mm')}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          {solarWebData ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">System ID</span>
                <span className="font-medium">
                  {solarWebData?.data?.pv_system_id ?? 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Battery Level</span>
                <span className="font-medium">
                  {solarWebData?.data?.storage?.[0]?.StateOfCharge_Relative ?? 'N/A'}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Solar Production</span>
                <span className="font-medium">{solarWebData?.data?.powerflow?.PV ?? 'N/A'} kW</span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No data available</p>
          )}
        </div>

        {/* Solcast Data */}
        <div 
          className="card cursor-pointer hover:shadow-lg transition-shadow duration-200"
          onClick={() => setShowSolcastPanel(true)}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Solcast</h2>
            <div className="flex items-center space-x-2">
              {services.find(s => s.service === 'solcast')?.status === 'healthy' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="text-sm text-gray-500">
                {format(new Date(solcastData?.timestamp || Date.now()), 'MMM dd HH:mm')}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          {solcastData ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Today</span>
                <span className="font-medium">
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tomorrow</span>
                </div>
            </div>
          ) : (
            <p className="text-gray-500">No data available</p>
          )}
        </div>

        {/* OpenMeteo Weather Data */}
        <div 
          className="card cursor-pointer hover:shadow-lg transition-shadow duration-200"
          onClick={() => setShowOpenMeteoPanel(true)}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">OpenMeteo Weather</h2>
            <div className="flex items-center space-x-2">
              {services.find(s => s.service === 'openmeteo')?.status === 'healthy' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-500" />
              )}
              <span className="text-sm text-gray-500">
                {format(new Date(openMeteoData?.data?.metadata?.generated_at || Date.now()), 'MMM dd HH:mm')}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          
          {openMeteoData ? (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Temperature</span>
                <span className="font-medium">{openMeteoData?.data?.current_weather?.temperature_2m ?? 'N/A'}°C</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Humidity</span>
                <span className="font-medium">{openMeteoData?.data?.current_weather?.relative_humidity_2m ?? 'N/A'}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Wind Speed</span>
                <span className="font-medium">{openMeteoData?.data?.current_weather?.wind_speed_10m ?? 'N/A'} km/h{' '}
                  {openMeteoData?.data?.current_weather?.wind_direction_10m != null
                    ? `${degreesToCompass(openMeteoData.data.current_weather.wind_direction_10m)}`
                    : 'N/A'}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No data available</p>
          )}
        </div>

      </div>

      {/* Service Status Overview */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Status Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {services.map((service) => (
            <div key={service.service} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-3">
                {service.status === 'healthy' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {service.service.replace('_', ' ')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {format(new Date(service.last_check), 'MMM dd, HH:mm')}
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                service.status === 'healthy' 
                  ? 'text-green-600 bg-green-50' 
                  : 'text-red-600 bg-red-50'
              }`}>
                {service.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* SolarWeb Data Panel */}
      {showSolarWebPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">SolarWeb Data</h3>
              <button
                onClick={() => setShowSolarWebPanel(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(80vh-80px)]">
              {latestData?.data?.services ? (
                (() => {
                  const solarwebService = latestData.data.services.find((service: any) => service.service === 'solarweb');
                  return solarwebService ? (
                    <JsonViewer 
                      data={solarwebService} 
                      title="SolarWeb Service Data"
                      defaultExpanded={false}
                      maxDepth={5}
                    />
                  ) : (
                    <p className="text-gray-500">No SolarWeb data available in selected entry</p>
                  );
                })()
              ) : (
                <p className="text-gray-500">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Amber Data Panel */}
      {showAmberPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Amber Electricity Data</h3>
              <button
                onClick={() => setShowAmberPanel(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(80vh-80px)]">
              {latestData?.data?.services ? (
                (() => {
                  const amberService = latestData.data.services.find((service: any) => service.service === 'amber');
                  return amberService ? (
                    <JsonViewer 
                      data={amberService} 
                      title="Amber Service Data"
                      defaultExpanded={false}
                      maxDepth={4}
                    />
                  ) : (
                    <p className="text-gray-500">No Amber data available in selected entry</p>
                  );
                })()
              ) : (
                <p className="text-gray-500">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Solcast Data Panel */}
      {showSolcastPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Solcast Data</h3>
              <button
                onClick={() => setShowSolcastPanel(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(80vh-80px)]">
              {latestData?.data?.services ? (
                (() => {
                  const solcastService = latestData.data.services.find((service: any) => service.service === 'solcast');
                  return solcastService ? (
                    <JsonViewer 
                      data={solcastService} 
                      title="Solcast Service Data"
                      defaultExpanded={false}
                      maxDepth={4}
                    />
                  ) : (
                    <p className="text-gray-500">No Solcast data available in selected entry</p>
                  );
                })()
              ) : (
                <p className="text-gray-500">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* OpenMeteo Data Panel */}
      {showOpenMeteoPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-lg shadow-2xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">OpenMeteo Weather Data</h3>
              <button
                onClick={() => setShowOpenMeteoPanel(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(80vh-80px)]">
              {latestData?.data?.services ? (
                (() => {
                  const openMeteoService = latestData.data.services.find((service: any) => service.service === 'openmeteo');
                  return openMeteoService ? (
                    <JsonViewer 
                      data={openMeteoService} 
                      title="OpenMeteo Service Data"
                      defaultExpanded={false}
                      maxDepth={4}
                    />
                  ) : (
                    <p className="text-gray-500">No OpenMeteo data available in selected entry</p>
                  );
                })()
              ) : (
                <p className="text-gray-500">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ServicesPage; 